'use client';

import { getProblemTypeTitle } from '@/constants/problem-type-titles';
import { ProductAiAnalysisReviewEntity } from '@/entities/product-ai-analysis.entity';
import { ReviewAiApiProblemEntity } from '@/entities/review.entity';
import { formatDateTimePartsInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import { formatReviewTableTextForDisplay } from '@/shared/format-review-table-text-for-display';
import Modal from '@/ui/Modal';
import Rating from '@/ui/Rating';
import clsx from 'clsx';
import { FC, useEffect, useMemo, useState } from 'react';

type ModalProductAiResponseDataProps = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    productName: string;
    loading: boolean;
    error: string | null;
    reviews: ProductAiAnalysisReviewEntity[];
};

const sentimentLabelMap: Record<string, string> = {
    positive: 'Позитив',
    negative: 'Негатив',
    neutral: 'Нейтрально',
};

const sentimentClassMap: Record<string, string> = {
    positive: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    negative: 'border-rose-200 bg-rose-50 text-rose-700',
    neutral: 'border-slate-200 bg-slate-100 text-slate-700',
};

const normalizeProblems = (value: unknown): ReviewAiApiProblemEntity[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is ReviewAiApiProblemEntity => {
        return typeof item === 'object' && item !== null;
    });
};

const renderValue = (value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') {
        return '—';
    }

    return value;
};

const formatReviewLabel = (review: ProductAiAnalysisReviewEntity): string => {
    const dateTime = formatDateTimePartsInGmtPlus3(review.createdDate);
    const dateLabel = dateTime ? `${dateTime.date} ${dateTime.time}` : review.createdDate;
    const reviewText = formatReviewTableTextForDisplay(review).replace(/\s+/g, ' ').trim();
    const shortReviewText = reviewText.length > 56 ? `${reviewText.slice(0, 56)}...` : reviewText;
    const textLabel = shortReviewText || '[без текста]';

    return `${dateLabel} • ${review.rating}/5 • ${textLabel}`;
};

const ModalProductAiResponseData: FC<ModalProductAiResponseDataProps> = ({
    isShown,
    setIsShown,
    productName,
    loading,
    error,
    reviews,
}) => {
    const [selectedReviewId, setSelectedReviewId] = useState<string>('');

    useEffect(() => {
        if (!reviews.length) {
            setSelectedReviewId('');
            return;
        }

        setSelectedReviewId((currentReviewId) =>
            reviews.some((review) => review.id === currentReviewId) ? currentReviewId : reviews[0].id,
        );
    }, [reviews]);

    const selectedReview = useMemo(() => {
        if (!reviews.length) {
            return null;
        }

        return reviews.find((review) => review.id === selectedReviewId) || reviews[0];
    }, [reviews, selectedReviewId]);

    const reviewData = selectedReview?.aiApiResponse.review;
    const usageData = selectedReview?.aiApiResponse.usage;
    const problems = normalizeProblems(reviewData?.problems);
    const selectedReviewText = selectedReview ? formatReviewTableTextForDisplay(selectedReview).trim() : '';
    const selectedReviewDateTime = selectedReview ? formatDateTimePartsInGmtPlus3(selectedReview.createdDate) : null;

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title={`AI анализ отзывов: ${productName}`}
            closable
            className="h-[90vh] w-[94vw] max-w-[1100px]"
        >
            <div className="h-full w-full min-h-0 overflow-auto p-4">
                {loading && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                        Загрузка AI-анализа...
                    </div>
                )}
                {!loading && error && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        {error}
                    </div>
                )}
                {!loading && !error && !selectedReview && (
                    <div className="rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500">
                        Нет отзывов с AI-анализом для выбранной группы.
                    </div>
                )}
                {!loading && !error && selectedReview && (
                    <div className="flex flex-col gap-4 text-sm leading-5 text-slate-800">
                        <section className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                                <div className="w-full min-w-0">
                                    <p className="text-xs text-slate-500">Отзыв</p>
                                    <select
                                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-xs text-slate-800 outline-none transition-colors focus:border-slate-500"
                                        value={selectedReview.id}
                                        onChange={(event) => setSelectedReviewId(event.target.value)}
                                    >
                                        {reviews.map((review) => (
                                            <option key={review.id} value={review.id}>
                                                {formatReviewLabel(review)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="min-w-[210px] rounded-md border border-slate-200 bg-slate-50 p-2">
                                    <p className="text-xs text-slate-500">Дата</p>
                                    <p className="text-xs font-medium text-slate-700">
                                        {selectedReviewDateTime
                                            ? `${selectedReviewDateTime.date} ${selectedReviewDateTime.time}`
                                            : renderValue(selectedReview.createdDate)}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2">
                                        <p className="text-xs text-slate-500">Оценка:</p>
                                        <Rating value={selectedReview.rating} size={12} />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-xs text-slate-500">Текст отзыва</p>
                                <p className="whitespace-pre-line break-words text-xs text-slate-700">
                                    {selectedReviewText || '—'}
                                </p>
                            </div>
                        </section>

                        <section className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="grid gap-2 md:grid-cols-3">
                                <div>
                                    <p className="text-xs text-slate-500">Тип</p>
                                    <p className="font-medium">
                                        {renderValue(getProblemTypeTitle(reviewData?.type_primary))}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Главная проблема</p>
                                    <p className="font-medium">
                                        {renderValue(getProblemTypeTitle(reviewData?.problem_primary))}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Вторичная проблема</p>
                                    <p className="font-medium">
                                        {renderValue(getProblemTypeTitle(reviewData?.problem_secondary))}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-xs text-slate-500">Черновик ответа из аналитики</p>
                                <p className="whitespace-pre-line break-words">{renderValue(reviewData?.answer)}</p>
                            </div>
                        </section>

                        <section className="flex flex-col gap-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Проблемы</h3>
                            {problems.length ? (
                                <div className="flex flex-col gap-2">
                                    {problems.map((problem, index) => {
                                        const sentiment = problem.sentiment || '';
                                        const sentimentLabel = sentimentLabelMap[sentiment] || sentiment || '—';

                                        return (
                                            <article
                                                key={`${problem.problem || 'problem'}-${index}`}
                                                className="rounded-lg border border-slate-200 p-3"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <p className="font-semibold">
                                                        {renderValue(getProblemTypeTitle(problem.problem))}
                                                    </p>
                                                    <span
                                                        className={clsx(
                                                            'rounded-full border px-2 py-0.5 text-xs',
                                                            sentimentClassMap[sentiment] || sentimentClassMap.neutral,
                                                        )}
                                                    >
                                                        {sentimentLabel}
                                                    </span>
                                                </div>
                                                <div className="mt-2 grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                                                    <p>Intensity: {renderValue(problem.intensity)}</p>
                                                    <p>Actionability: {renderValue(problem.actionability)}</p>
                                                    <p>Confidence: {renderValue(problem.confidence)}</p>
                                                </div>
                                                <div className="mt-2 space-y-1 text-xs text-slate-700">
                                                    <p>
                                                        <span className="text-slate-500">Evidence: </span>
                                                        {renderValue(problem.evidence)}
                                                    </p>
                                                    <p>
                                                        <span className="text-slate-500">Model: </span>
                                                        {renderValue(problem.market_model)}
                                                    </p>
                                                    <p>
                                                        <span className="text-slate-500">Model desc: </span>
                                                        {renderValue(problem.model_desc)}
                                                    </p>
                                                    <p>
                                                        <span className="text-slate-500">Action: </span>
                                                        {renderValue(problem.proposed_action)}
                                                    </p>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500">
                                    Нет данных по проблемам
                                </p>
                            )}
                        </section>

                        <section className="rounded-lg border border-slate-200 bg-white p-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usage</h3>
                            <div className="mt-2 grid gap-2 text-xs text-slate-700 md:grid-cols-3">
                                <p>Prompt: {renderValue(usageData?.prompt_tokens)}</p>
                                <p>Completion: {renderValue(usageData?.completion_tokens)}</p>
                                <p>Total: {renderValue(usageData?.total_tokens)}</p>
                            </div>
                            <p className="mt-2 text-xs text-slate-600">
                                Reasoning: {renderValue(usageData?.reasoning_tokens)}
                            </p>
                        </section>

                        <details className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <summary className="cursor-pointer text-xs font-medium text-slate-700">Raw JSON</summary>
                            <pre className="mt-2 overflow-auto rounded bg-white p-2 text-[11px] leading-4 text-slate-800">
                                {JSON.stringify(selectedReview.aiApiResponse, null, 2)}
                            </pre>
                        </details>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ModalProductAiResponseData;
